import React, { useState } from 'react';
import type { AdminAccount } from '../../lib/types';
import { isValidEmail, isValidPassword } from '../../lib/validation';

interface AccountsSectionProps {
  accounts: AdminAccount[];
  currentAdminId: string;
  onUpdateAccount: (data: {
    accountId: string;
    email?: string;
    newPassword?: string;
    isActive?: boolean;
  }) => Promise<void>;
}

export const AccountsSection: React.FC<AccountsSectionProps> = ({
  accounts,
  currentAdminId,
  onUpdateAccount,
}) => {
  // Modal / form states for changing email or password
  const [targetAccount, setTargetAccount] = useState<AdminAccount | null>(null);
  const [modalMode, setModalMode] = useState<'email' | 'password' | null>(null);

  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const openEmailModal = (acc: AdminAccount) => {
    setTargetAccount(acc);
    setNewEmail(acc.email);
    setModalMode('email');
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  const openPasswordModal = (acc: AdminAccount) => {
    setTargetAccount(acc);
    setNewPassword('');
    setConfirmPassword('');
    setModalMode('password');
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  const closeModal = () => {
    setModalMode(null);
    setTargetAccount(null);
    setErrorMsg(null);
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetAccount) return;
    setErrorMsg(null);

    const trimmed = newEmail.trim().toLowerCase();
    if (!isValidEmail(trimmed)) {
      setErrorMsg('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdateAccount({
        accountId: targetAccount.id,
        email: trimmed,
      });
      setSuccessMsg(`Email successfully updated for ${targetAccount.role}`);
      closeModal();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to update email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!targetAccount) return;
    setErrorMsg(null);

    if (!isValidPassword(newPassword)) {
      setErrorMsg('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setErrorMsg('Passwords do not match');
      return;
    }

    setIsSubmitting(true);
    try {
      await onUpdateAccount({
        accountId: targetAccount.id,
        newPassword,
      });
      setSuccessMsg(`Password successfully changed for ${targetAccount.role}`);
      closeModal();
    } catch (err: unknown) {
      setErrorMsg(err instanceof Error ? err.message : 'Failed to change password');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (acc: AdminAccount) => {
    if (acc.id === currentAdminId) {
      alert('You cannot deactivate your own administrator account');
      return;
    }

    try {
      await onUpdateAccount({
        accountId: acc.id,
        isActive: !acc.is_active,
      });
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to toggle account status');
    }
  };

  return (
    <div className="admin-section accounts-section">
      <div className="section-header">
        <h2>Account Management</h2>
        <p className="section-description">
          Manage identity, credentials, and access for the two designated keepsake accounts.
        </p>
      </div>

      {successMsg && <div className="notice-banner banner-success">{successMsg}</div>}

      <div className="accounts-cards-grid">
        {accounts.map(acc => {
          const isSelf = acc.id === currentAdminId;
          return (
            <div key={acc.id} className="account-card">
              <div className="account-card-header">
                <div>
                  <span className={`role-badge role-${acc.role}`}>{acc.role}</span>
                  <h3 className="account-email">{acc.email}</h3>
                </div>
                <div>
                  {isSelf ? (
                    <span className="status-pill status-active">Active (You)</span>
                  ) : (
                    <button
                      type="button"
                      className={`status-pill ${acc.is_active ? 'status-active' : 'status-disabled'}`}
                      onClick={() => handleToggleActive(acc)}
                      title="Click to toggle account access"
                    >
                      {acc.is_active ? 'Active' : 'Disabled'}
                    </button>
                  )}
                </div>
              </div>

              <div className="account-details">
                <div className="detail-row">
                  <span className="detail-label">Status</span>
                  <span className="detail-val">
                    {acc.is_active ? 'Permitted to authenticate' : 'Access suspended'}
                  </span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Created</span>
                  <span className="detail-val">{new Date(acc.created_at).toLocaleDateString()}</span>
                </div>
                <div className="detail-row">
                  <span className="detail-label">Last Updated</span>
                  <span className="detail-val">{new Date(acc.updated_at).toLocaleDateString()}</span>
                </div>
              </div>

              <div className="account-actions">
                <button
                  type="button"
                  className="action-btn action-btn-secondary"
                  onClick={() => openEmailModal(acc)}
                >
                  Change Email
                </button>
                <button
                  type="button"
                  className="action-btn action-btn-secondary"
                  onClick={() => openPasswordModal(acc)}
                >
                  Change Password
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal for Email or Password Change */}
      {modalMode && targetAccount && (
        <div className="admin-modal-backdrop" onClick={closeModal}>
          <div className="admin-modal-card" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                {modalMode === 'email' ? 'Change Email Address' : 'Change Account Password'} —{' '}
                <span className="text-muted">{targetAccount.role}</span>
              </h3>
              <button type="button" className="close-btn" onClick={closeModal}>
                Close
              </button>
            </div>

            {errorMsg && <div className="form-error-inline">{errorMsg}</div>}

            {modalMode === 'email' ? (
              <form onSubmit={handleEmailSubmit} className="modal-form">
                <div className="form-field">
                  <label htmlFor="new-email">New Email Address</label>
                  <input
                    id="new-email"
                    type="email"
                    value={newEmail}
                    onChange={e => setNewEmail(e.target.value)}
                    disabled={isSubmitting}
                    required
                    className="input-field"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="action-btn action-btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="action-btn action-btn-primary">
                    {isSubmitting ? 'Saving...' : 'Update Email'}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={handlePasswordSubmit} className="modal-form">
                <div className="form-field">
                  <label htmlFor="new-pwd">New Password (min 8 characters)</label>
                  <input
                    id="new-pwd"
                    type="password"
                    value={newPassword}
                    onChange={e => setNewPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                    className="input-field"
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="confirm-pwd">Confirm New Password</label>
                  <input
                    id="confirm-pwd"
                    type="password"
                    value={confirmPassword}
                    onChange={e => setConfirmPassword(e.target.value)}
                    disabled={isSubmitting}
                    required
                    className="input-field"
                  />
                </div>

                <div className="modal-actions">
                  <button type="button" className="action-btn action-btn-secondary" onClick={closeModal}>
                    Cancel
                  </button>
                  <button type="submit" disabled={isSubmitting} className="action-btn action-btn-primary">
                    {isSubmitting ? 'Saving...' : 'Update Password'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
